import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { LicensePlateComponent } from '../../../license-plate.component';
import { getCompositeDisplayValue } from '../../composite-subfields.util';
import { ValueDisplayComponent } from './value-display.component';

/**
 * Read display for an RDW answer (#21239): the licence plate as a plate.
 *
 * The plate comes from the SHARED composite rule (`getCompositeDisplayValue`, rdw → `plate`),
 * the same primary the table cell and the review card pick out of the object — never a second
 * opinion about which sub-key is the answer.
 *
 * `getCompositeDisplayValue` rather than the full `formatDisplayValue`: when the plate is empty
 * the shared formatter joins the OTHER vehicle fields ("DAF XG+ 530"), and printing that inside
 * a plate graphic would be a lie. Those answers fall through to the text display below instead,
 * which keeps manual RDW entry readable without inventing a plate for it.
 *
 * Presentational: both inputs come from the row.
 */
@Component({
  selector: 'dd-license-plate-display',
  imports: [LicensePlateComponent, ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the display must not become a box inside the value span, or the row's
  // truncate/multiline behaviour would stop at this element.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (plate(); as plate) {
      <dd-license-plate [plate]="plate" />
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class LicensePlateDisplayComponent extends ReadonlyDisplayBase {
  /** The registration, or `null` when this answer has none (empty, or manual data only). */
  readonly plate = computed(() => {
    const plate = getCompositeDisplayValue(this.field().type, this.value());
    return plate?.trim() ? plate.trim() : null;
  });
}
